import { Shield, Award, Heart, Lightbulb, Target, Users } from "lucide-react";

interface ValuesProps {
  language: 'en' | 'de';
}

export const Values = ({ language }: ValuesProps) => {
  const content = {
    en: {
      title: "What drives us",
      subtitle: "Core values that guide every engagement",
      values: [
        {
          icon: Shield,
          title: "Trust",
          description: "Building lasting relationships through reliability, transparency, and consistent delivery. Trust is the foundation of every successful partnership."
        },
        {
          icon: Award,
          title: "Confidence",
          description: "Combining deep technical competence with the self-assurance to tackle complex challenges. Confident execution backed by proven expertise."
        },
        {
          icon: Heart,
          title: "Integrity",
          description: "Guided by philanthropy and good intentions, always putting client success first. Doing what's right, not just what's easy."
        },
        {
          icon: Lightbulb,
          title: "Innovation",
          description: "The future is uncertain, but we shape it. Embracing cutting-edge technologies and creative approaches to solve tomorrow's challenges today."
        },
        {
          icon: Target,
          title: "Meaning",
          description: "Making a difference and leaving a track record of impactful solutions. Every project is an opportunity to create lasting value."
        },
        {
          icon: Users,
          title: "Exclusivity",
          description: "It is okay to agree to disagree. Respecting diverse perspectives while maintaining high standards and selective partnerships."
        }
      ]
    },
    de: {
      title: "Was uns antreibt",
      subtitle: "Grundwerte, die jedes Engagement leiten",
      values: [
        {
          icon: Shield,
          title: "Vertrauen",
          description: "Aufbau dauerhafter Beziehungen durch Zuverlässigkeit, Transparenz und konsistente Leistung. Vertrauen ist die Grundlage jeder erfolgreichen Partnerschaft."
        },
        {
          icon: Award,
          title: "Selbstvertrauen",
          description: "Kombination aus fundierter technischer Kompetenz und dem Selbstvertrauen, komplexe Herausforderungen anzugehen. Selbstbewusste Umsetzung auf Basis bewährter Expertise."
        },
        {
          icon: Heart,
          title: "Integrität",
          description: "Geleitet von Philanthropie und guten Absichten, immer mit dem Erfolg des Kunden im Fokus. Das Richtige tun, nicht nur das Einfache."
        },
        {
          icon: Lightbulb,
          title: "Innovation",
          description: "Die Zukunft ist ungewiss, aber wir gestalten sie. Einsatz modernster Technologien und kreativer Ansätze zur Lösung der Herausforderungen von morgen."
        },
        {
          icon: Target,
          title: "Bedeutung",
          description: "Einen Unterschied machen und eine Erfolgsbilanz wirkungsvoller Lösungen hinterlassen. Jedes Projekt ist eine Gelegenheit, dauerhaften Wert zu schaffen."
        },
        {
          icon: Users,
          title: "Exklusivität",
          description: "Es ist in Ordnung, unterschiedlicher Meinung zu sein. Respekt für verschiedene Perspektiven bei gleichzeitiger Wahrung hoher Standards und selektiver Partnerschaften."
        }
      ]
    }
  };

  const currentContent = content[language];

  return (
    <section id="values" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {currentContent.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentContent.values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-card border border-border hover:border-accent/50 transition-smooth hover:shadow-elegant"
              >
                <div className="mb-6 p-4 rounded-lg bg-accent/10 w-fit group-hover:bg-accent/20 transition-smooth">
                  <Icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-2xl font-semibold mb-4">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
